import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { esPA } from '@cumpli/shared';
import { api } from './api';
import { useToast } from './use-toast';
import type { ExpedienteDoc } from './phase1';
import { retryUnlessNotFound } from './view-state';

export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 5 * 60_000, retry: false });
}

export function useRoles() {
  return useQuery({ queryKey: ['roles'], queryFn: api.roles, staleTime: 60 * 60_000 });
}

export function useUsers(params: { search?: string; status?: string }) {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () => api.listUsers(params),
  });
}

export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: ['users', id],
    queryFn: () => api.getUser(id!),
    enabled: !!id,
    retry: retryUnlessNotFound,
  });
}

export function useTenants() {
  return useQuery({ queryKey: ['tenants'], queryFn: api.listTenants });
}

/** Phase-1 preview (demo data — see lib/phase1.ts). */
export function useExpedientes() {
  return useQuery({ queryKey: ['expedientes'], queryFn: api.listExpedientes });
}

export function useExpediente(id: string | undefined) {
  return useQuery({
    queryKey: ['expedientes', id],
    queryFn: () => api.getExpediente(id!),
    enabled: !!id,
  });
}

/**
 * An expediente's UNIFIED activity timeline (audit-backed, restore-decorated)
 * — every audited action across the expediente AND its documents/subjects/
 * owners/reviews/consents/alerts, newest first, cursor-paginated. Entries
 * paired with a `expediente_versions` projection row carry `versionSeq` +
 * `restorable: true` (see `ExpedienteActivityEntry`), so this ONE feed powers
 * both the informational log AND the restore affordances on `ExpedienteDetail`
 * — there is no longer a separate "Versiones" query. `useInfiniteQuery` so
 * pages beyond the first are reachable via `fetchNextPage`/`hasNextPage`.
 */
export function useExpedienteActivity(id: string | undefined) {
  return useInfiniteQuery({
    queryKey: ['expedientes', id, 'activity'],
    queryFn: ({ pageParam }) => api.getExpedienteActivity(id!, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!id,
  });
}

/**
 * A single version-projection row's full case-aggregate before/after
 * snapshot, fetched by `versionSeq` — powers the drawer's "Expediente
 * completo" tab. Lazy: only enabled once the caller has both a concrete
 * `versionSeq` (the entry is `restorable`) AND `enabled` is true (the tab has
 * actually been opened), so it never fires just from opening the Cambios tab.
 */
export function useExpedienteVersionDetail(
  id: string | undefined,
  versionSeq: number | null,
  enabled: boolean
) {
  return useQuery({
    queryKey: ['expedientes', id, 'versions', versionSeq],
    queryFn: () => api.getExpedienteVersionDetail(id!, versionSeq!),
    enabled: !!id && versionSeq !== null && enabled,
  });
}

/**
 * Invalidates BOTH CQRS-backed views into an expediente's history — the
 * unified activity/timeline feed and the (per-versionSeq) version-detail
 * cache — so any write to the expediente or its documents shows up live in
 * "Actividad y versiones" without a manual reload. This is the fix for the
 * reported bug (deleting a document didn't appear in Actividad until reload):
 * every mutation that touches an expediente or its documents must call this
 * IN ADDITION to whatever else it already invalidates. Exported as a plain
 * function (not a hook) so both `useMutation` hooks here AND the ad-hoc
 * `qc.invalidateQueries` call sites in components can share the one
 * definition instead of re-deriving these two query keys.
 */
export function invalidateExpedienteTimeline(qc: QueryClient, expedienteId: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['expedientes', expedienteId, 'activity'] }),
    qc.invalidateQueries({ queryKey: ['expedientes', expedienteId, 'versions'] }),
  ]);
}

/**
 * Shared invalidation after any restore action from the unified timeline
 * drawer: the timeline itself (via `invalidateExpedienteTimeline`), the
 * expediente (detail + list), its documents (prefix — covers the active AND
 * deleted/per-person variants), the papelera, and alertas (a restored
 * expediente/document can clear or reopen an alert that referenced it).
 */
function useInvalidateAfterRestore(expedienteId: string) {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      invalidateExpedienteTimeline(qc, expedienteId),
      qc.invalidateQueries({ queryKey: ['expedientes', expedienteId] }),
      qc.invalidateQueries({ queryKey: ['expedientes', expedienteId, 'documents'] }),
      qc.invalidateQueries({ queryKey: ['expedientes'] }),
      qc.invalidateQueries({ queryKey: ['papelera'] }),
      qc.invalidateQueries({ queryKey: ['alertas'] }),
    ]);
}

/** Re-apply a prior version's full aggregate snapshot (incl. estado/approval)
 *  as a NEW audited edit — the risk score is always re-derived server-side,
 *  never resurrected verbatim. */
export function useRestoreExpedienteVersion(expedienteId: string) {
  const toast = useToast();
  const invalidate = useInvalidateAfterRestore(expedienteId);
  return useMutation({
    mutationFn: (versionSeq: number) => api.restoreExpedienteVersion(expedienteId, versionSeq),
    onSuccess: async () => {
      await invalidate();
      toast({ message: esPA.expedientes.versionRestored, kind: 'success' });
    },
    onError: () => toast({ message: 'No se pudo restaurar la versión', kind: 'error' }),
  });
}

/** Undelete a single soft-deleted document (its files are conserved). */
export function useRestoreExpedienteDocument(expedienteId: string) {
  const toast = useToast();
  const invalidate = useInvalidateAfterRestore(expedienteId);
  return useMutation({
    mutationFn: (docId: string) => api.restoreDocument(expedienteId, docId),
    onSuccess: async () => {
      await invalidate();
      toast({ message: 'Documento restaurado', kind: 'success' });
    },
    onError: () => toast({ message: 'No se pudo restaurar el documento', kind: 'error' }),
  });
}

/** Undelete the expediente itself (out of the papelera). */
export function useRestoreExpedienteFromActivity(expedienteId: string) {
  const toast = useToast();
  const invalidate = useInvalidateAfterRestore(expedienteId);
  return useMutation({
    mutationFn: () => api.restoreExpediente(expedienteId),
    onSuccess: async () => {
      await invalidate();
      toast({ message: 'Expediente restaurado', kind: 'success' });
    },
    onError: () => toast({ message: 'No se pudo restaurar el expediente', kind: 'error' }),
  });
}

/**
 * Tenant settings — the firm's `entityMode` (sector del sujeto obligado). Read-only
 * in the registration wizard (D1: sector is a tenant attribute, not per-expediente);
 * cached because it changes rarely.
 */
export function useTenantSettings() {
  return useQuery({
    queryKey: ['tenant-settings'],
    queryFn: api.getTenantSettings,
    staleTime: 5 * 60_000,
  });
}

/**
 * Reverse ownership links: the entities for which this expediente's subject is a
 * registered beneficiario final. Empty for most expedientes; a natural-person
 * UBO's own expediente surfaces its parent entities through this.
 */
export function useVinculos(id: string | undefined) {
  return useQuery({
    queryKey: ['expedientes', id, 'vinculos'],
    queryFn: () => api.getVinculos(id!),
    enabled: !!id,
  });
}

export function useOnboardingTrend() {
  return useQuery({
    queryKey: ['onboarding-trend'],
    queryFn: api.onboardingTrend,
    staleTime: 5 * 60_000,
  });
}

export function useAlerts() {
  return useQuery({ queryKey: ['alertas'], queryFn: api.listAlerts });
}

/**
 * The copilot's RBAC-filtered capability catalog (explore cards + "/" menu).
 * The catalog is static per session, so it's cached indefinitely and retried
 * once; callers fall back to hiding their surfaces when it's absent, so a failure
 * never crashes the page.
 */
export function useAssistantTools() {
  return useQuery({
    queryKey: ['assistant-tools'],
    queryFn: api.getAssistantTools,
    staleTime: Infinity,
    retry: 1,
  });
}

/**
 * §3 — expedientes whose debida-diligencia renewal is due (or within `windowDays`).
 * Read-only for everyone; opening the alarms (renewal.run) is a separate mutation.
 */
export function useDueRenewals(windowDays = 30) {
  return useQuery({
    queryKey: ['renovaciones', 'due', windowDays],
    queryFn: () => api.listDueRenewals(windowDays),
  });
}

/** "Alcance a clientes" — suggested/scheduled/sent outreach messages, optionally filtered by status. */
export function useOutreach(status?: string) {
  return useQuery({
    queryKey: ['alcance', status],
    queryFn: () => api.listOutreach(status),
  });
}

export function useAlert(id: string | undefined) {
  return useQuery({
    queryKey: ['alertas', id],
    queryFn: () => api.getAlert(id!),
    enabled: !!id,
  });
}

/** A document plus the client it belongs to (for the tenant-wide catalog). */
export interface TenantDocument extends ExpedienteDoc {
  cliente: string;
  expId: string;
}

/**
 * Tenant-wide document catalog. There is no single "all documents" endpoint yet,
 * so fan out over the expedientes list and merge each one's documents. Fine for
 * the current volume; a dedicated backend endpoint would scale better.
 */
export function useAllDocuments() {
  const expQ = useExpedientes();
  const exps = expQ.data;
  const docsQ = useQuery({
    queryKey: ['all-documents', (exps ?? []).map((e) => e.id)],
    enabled: exps !== undefined,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<TenantDocument[]> => {
      const lists = await Promise.all(
        (exps ?? []).map(async (e) => {
          const docs = await api.listDocuments(e.id);
          return docs.map((d) => ({ ...d, cliente: e.name, expId: e.id }));
        })
      );
      return lists.flat();
    },
  });
  return {
    data: docsQ.data,
    isLoading: expQ.isLoading || docsQ.isLoading,
    isError: expQ.isError || docsQ.isError,
  };
}

/**
 * es-PA date formatting (§9). Una fecha no parseable NO puede tumbar la pantalla:
 * `Intl.format(new Date('05 dic 2029'))` lanza `RangeError: Invalid time value` y,
 * sin error boundary, deja la app en blanco (pasaba al abrir un expediente con
 * documentos, porque los meses es-PA sin equivalente inglés —'dic', 'ago', 'ene'—
 * no parsean). Se devuelve el valor crudo, que ya suele ser texto legible, en vez
 * de fallar. // ponytail: mismo guard que ya tiene `formatRelative` justo abajo.
 */
export function formatDate(iso: string | null, fallback: string): string {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('es-PA', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(d);
}

/** es-PA relative time from now, e.g. "dentro de 2 horas" / "hace 5 minutos". */
export function formatRelative(iso: string | null): string {
  if (!iso) return '';
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return '';
  const diffMs = target - Date.now();
  const MIN = 60_000;
  const HOUR = 3_600_000;
  const DAY = 86_400_000;
  const rtf = new Intl.RelativeTimeFormat('es-PA', { numeric: 'auto' });
  // Threshold on the rounded value so 59.9 min says "1 hora", not "60 minutos".
  const minutes = Math.round(diffMs / MIN);
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
  const hours = Math.round(diffMs / HOUR);
  if (Math.abs(hours) < 24) return rtf.format(hours, 'hour');
  return rtf.format(Math.round(diffMs / DAY), 'day');
}
